import React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'gatsby'
import { useWindowSize } from 'react-use'

import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

import ButtonPrimary from './Buttons/ButtonPrimary'
import Video from './Video'
import AccordionItem from './AccordionItem'

import { generatePath } from './../utils/helpers'

const TextStyles = css`
    margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.5);

    ${props => props.theme.above.desktop`
        margin-bottom: calc(${props.theme.sizes.desktop} / 1.5);
    `}
`

const Heading4 = styled.h4`
    ${TextStyles}

    font-family: ${props => props.theme.fontFamilies.plainRegular};
    font-size: ${props => props.theme.fontSizes.mobile.p};
    line-height: 1.2;

    ${props => props.theme.above.desktop`
        font-size: ${props.theme.fontSizes.desktop.h5};
    `}
`

const Heading5 = styled.h5`
    ${TextStyles}

    font-family: ${props => props.theme.fontFamilies.plainLight};
    font-size: ${props => props.theme.fontSizes.mobile.p};
    line-height: 1.4;

    ${props => props.theme.above.desktop`
        font-size: ${props.theme.fontSizes.desktop.h6};
    `}
`

const Paragraph = styled.p`
    ${TextStyles}

    font-family: ${props => props.theme.fontFamilies.plainLight};
    font-size: ${props => props.theme.fontSizes.mobile.m};
    line-height: 1.4;

    b, strong {
        font-family: ${props => props.theme.fontFamilies.plainRegular};
    }

    &:empty {
        display: none;
    }

    &:last-of-type {
        margin-bottom: 0;
    }

    ${props => props.theme.above.desktop`
        font-size: ${props.theme.fontSizes.desktop.p};

        a, button {
            margin-top: calc(${props.theme.sizes.desktop} / 2);
        }
    `}
`

const UnorderedList = styled.ul`
    ${TextStyles}

    li {
        position: relative;

        padding-left: ${props => props.theme.mobileVW(25)};

        &:before {
            content: '—';
            position: absolute;

            margin-top: ${props => props.theme.mobileVW(8)};
            margin-left: -${props => props.theme.mobileVW(20)};

            font-size: ${props => props.theme.mobileVW(12)};
        }

        p {
            margin-bottom: 0;
        }
    }

    ${props => props.theme.above.desktop`
        li {
            padding-left: ${props.theme.desktopVW(25)};

            &:before {
                margin-top: ${props => props.theme.desktopVW(8)};
                margin-left: -${props => props.theme.desktopVW(20)};

                font-size: ${props => props.theme.desktopVW(12)};
            }
        }
    `}
`

const OrderedList = styled.ol`
    ${TextStyles}

    padding-left: ${props => props.theme.mobileVW(25)};

    list-style: decimal;

    font-size: ${props => props.theme.mobileVW(12)};

    ${props => props.theme.above.desktop`
        padding-left: ${props.theme.desktopVW(20)};

        font-size: ${props => props.theme.desktopVW(15)};
    `}
`

const StyledButtonPrimary = styled(ButtonPrimary)`
    /* color: currentColor;

    border-color: currentColor; */
`

const StyledLink = styled.a`
    color: currentColor;

    transition: color 0.5s cubic-bezier(.16,1.08,.38,.98);

    &:hover {
        color: ${props => props.theme.colors.orange};
    }
`

const Media = styled.div`
    position: relative;
    
    margin-top: ${props => props.theme.sizes.mobile};
    padding-top: 56%;

    ${props => props.theme.above.desktop`
        margin-top: calc(${props.theme.sizes.desktop} * 2);
    `}
`

const StyledVideo = styled(Video)`
    ${props => props.theme.styles.element.fill};

    .video {
        position: relative;

        top: initial;
        left: initial;

        transform: none;

        width: 100%;
        height: 100%;
    }
`

const ImageLink = styled.a``

const StyledImage = styled.img`
    display: block;

    width: 100%;

    margin: ${props => props.theme.sizes.mobile} 0;

    ${props => props.theme.above.desktop`
        margin: calc(${props.theme.sizes.desktop} * 2) 0;
    `}
`

const VideoTitle = styled.span`
    display: block;

    margin: calc(${props => props.theme.sizes.mobile} / 3) 0 ${props => props.theme.sizes.mobile} 0;

    font-family: ${props => props.theme.fontFamilies.plainLight};
    font-size: ${props => props.theme.fontSizes.mobile.xxs};

    text-align: right;

    opacity: 0.5;

    ${props => props.theme.above.desktop`
        margin: ${props.theme.sizes.desktop} 0 calc(${props.theme.sizes.desktop} * 2) 0;

        font-size: ${props.theme.fontSizes.desktop.p};
    `}
`

const getLocalizedField = field => {
    if (typeof field == 'undefined' || field == null) return null

    if (typeof field['en-US'] != 'undefined') {
        return field['en-US']
    }

    return field
}

const TextRenderer = ({
    lang,
    data,
    className,
    useInlineLink
}) => {

    const { width: windowWidth } = useWindowSize()

    const checkModal = uri => {
        if (uri.includes('get-in-touch') || uri.includes('self-test') || uri.includes('reserve-your-space') || uri.includes('join-us')) {
            return {
                modal: windowWidth < 1023 ? false : true,
                formInput: uri
            }
        } else {
            return false
        }
    }

    return (
        <>
            {data && documentToReactComponents(data.json, {
                renderNode: {
                    [BLOCKS.HEADING_1]: (node, children) => <h1 className={className}>{children}</h1>,
                    [BLOCKS.HEADING_2]: (node, children) => <h2 className={className}>{children}</h2>,
                    [BLOCKS.HEADING_3]: (node, children) => <h3 className={className}>{children}</h3>,
                    [BLOCKS.UL_LIST]: (node, children) => <UnorderedList className={className}>{children}</UnorderedList>,
                    [BLOCKS.OL_LIST]: (node, children) => <OrderedList className={className}>{children}</OrderedList>,
                    [BLOCKS.HEADING_4]: (node, children) => <Heading4 className={className}>{children}</Heading4>,
                    [BLOCKS.HEADING_5]: (node, children) => <Heading5 className={className}>{children}</Heading5>,
                    [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph className={className}>{children}</Paragraph>,
                    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
                        const { data: { target: { fields } } } = node

                        if (typeof fields == 'undefined' || fields.file['en-US'] == 'undefined') return

                        let link = false
                        
                        if (fields.title['en-US'].includes('Uber')) {
                            link = 'https://www.ubereats.com/nl-en/amsterdam/food-delivery/rockstar-kitchen/E5nm2Gj9QMS_J_mkFy9kAQ'
                        } else if (fields.title['en-US'].includes('Thuisbezorgd')) {
                            link = 'https://www.thuisbezorgd.nl/rockstar-kitchen'
                        } else if (fields.title['en-US'].includes('Deliveroo')) {
                            link = 'https://deliveroo.nl/en/menu/amsterdam/oost/rockstar-kitchen'
                        }
                        
                        if (link) {
                            return (
                                <ImageLink href={link} target='_blank' className='embedded-asset'>
                                    <StyledImage
                                        className='embedded-asset-image'
                                        src={fields.file['en-US'].url} 
                                        alt={fields.title['en-US'] || 'RL - Embedded Image'} 
                                    />
                                </ImageLink>
                            )
                        } else {
                            return (
                                <StyledImage 
                                    className='embedded-asset' 
                                    src={fields.file['en-US'].url} 
                                    alt={fields.title['en-US'] || 'RL - Embedded Image'} 
                                />
                            )
                        }
                    },
                    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
                        const { data: { target } } = node
                        const { fields, sys } = target
                        
                        if (typeof fields == 'undefined') return

                        const videoUrl = getLocalizedField(fields.videoUrl)
                        
                        if (videoUrl) {
                            return (
                                <>
                                    <Media>
                                        <StyledVideo
                                            className={className}
                                            url={videoUrl}
                                            title={getLocalizedField(fields.name) || 'RL - Embedded Video'}
                                            inline={true}
                                            inView={true}
                                        />
                                    </Media>
                                    {fields.videoSubtitle && (
                                        <VideoTitle>{getLocalizedField(fields.videoSubtitle)}</VideoTitle>
                                    )}
                                </>
                            )
                        }

                        const label = getLocalizedField(fields.toggleLabel)
                        const teaser = getLocalizedField(fields.teaser)
                        const content = getLocalizedField(fields.content)
                        const defaultOpen = getLocalizedField(fields.defaultOpen)

                        if (content) {
                            return (
                                <AccordionItem
                                    className={className}
                                    id={sys && sys.id}
                                    label={label || getLocalizedField(fields.componentTitle)}
                                    teaser={
                                        teaser && (
                                            <TextRenderer
                                                lang={lang}
                                                data={{ json: teaser }}
                                                className={className}
                                                useInlineLink={useInlineLink}
                                            />
                                        )
                                    }
                                    content={
                                        <TextRenderer
                                            lang={lang}
                                            data={{ json: content }}
                                            className={className}
                                            useInlineLink={useInlineLink}
                                        />
                                    }
                                    defaultOpen={defaultOpen}
                                />
                            )
                        }

                        return null
                    },
                    [INLINES.HYPERLINK]: (node, children) => {
                        const { data: { uri } } = node

                        if (uri.includes('mailto') || uri.includes('http') || uri.includes('tel:')) {
                            if (!useInlineLink) {
                                return (
                                    <StyledButtonPrimary
                                        href={uri}
                                        label={children}
                                        colored={true}
                                    />
                                )
                            } else {
                                return (
                                    <StyledLink 
                                        href={uri} 
                                        target={uri.includes('http') ? '_target' : ''}
                                    >{children}</StyledLink>
                                )
                            }
                        } else {
                            if (!useInlineLink) {
                                return (
                                    <StyledButtonPrimary
                                        to={generatePath(lang, uri.replace('/', ''))}
                                        label={children}
                                        colored={true}
                                        modal={checkModal(uri)}
                                    />
                                )
                            } else {
                                return (
                                    <Link
                                        to={generatePath(lang, uri.replace('/', ''))}
                                    >{children}</Link>
                                )
                            }
                        }
                    }
                }
            })}
        </>
    )
}

export default TextRenderer
